import { produce } from 'immer';
import { isBoolean } from 'lodash';

import type { HoldingsSorting } from '@/pages/Portfolio/hooks/useHoldingsSorting';
import { isHoldingsSorting } from '@/pages/Portfolio/hooks/useHoldingsSorting';

import { CountryISO } from '@/utilities/countries';

import { PeriodType, isPeriodType } from '@/components/PriceChart/utils';
import { FeedSortingOptions } from '@/components/Dashboard/hooks/useFeedSorting';
import { HoldingsSortingOptions } from '@/components/DeprecatedPortfolio/components/Holdings/components/HoldingList';
import {
  fetchUserProfile,
  updateUserProfile,
  hydrateUserProfile,
} from '@/components/User/redux/routines';

type UpdatingProfile = 'details' | 'idle';

export interface ProfileState {
  readonly id: string;
  readonly name: string;
  readonly group: number;
  readonly isLocal: boolean;
  readonly loggedIn: boolean;
  readonly currencyISO: string;
  readonly givenName: string;
  readonly familyName: string;
  readonly updatingProfile: UpdatingProfile;
  readonly countryISO: string;
  readonly emailExists: boolean;
  readonly accountType: string;
  readonly authProvider: string;
  readonly emailAddress: string;
  readonly referralCode: string;
  readonly registrationDate: number;
  readonly defaultMarketISO: string;
  readonly defaultFeedSorting: FeedSortingOptions;
  readonly showLiquidated: boolean;
  readonly hasSeenSAModal: boolean;
  readonly hasSeenSubscriptionModal: boolean;
  readonly hasSeenTrialEndModal: boolean;
  readonly defaultPortfolioId: string;
  readonly defaultWatchlistId: string | null;
  readonly holdingsSorting: string;
  readonly isWatchlistBannerDismissed?: boolean;
  readonly classificationResponses?: object;
  readonly portfolio?: {
    readonly holdingsPeriod?: PeriodType;
    readonly holdingsSorting?: HoldingsSorting;
    readonly links?: Record<
      string,
      { hasDismissedError: boolean; hasDismissedSuccess: boolean }
    >;
  };
}

export const getInitialProfileState = (): ProfileState => ({
  id: '',
  name: '',
  group: -1,
  isLocal: true,
  loggedIn: false,
  currencyISO: '',
  givenName: '',
  familyName: '',
  updatingProfile: 'idle',
  countryISO: '',
  emailExists: false,
  accountType: 'Simply Wall St',
  emailAddress: '',
  authProvider: '',
  referralCode: '',
  registrationDate: 0,
  defaultMarketISO: CountryISO.US,
  defaultFeedSorting: FeedSortingOptions.Newest,
  showLiquidated: false,
  hasSeenSAModal: false,
  hasSeenSubscriptionModal: false,
  hasSeenTrialEndModal: false,
  defaultPortfolioId: '',
  defaultWatchlistId: null,
  holdingsSorting: HoldingsSortingOptions.CapitalGainWeek,
  isWatchlistBannerDismissed: false,
});

export default function profileReducer(
  state = getInitialProfileState(),
  action: ReducerAction
): ProfileState {
  switch (action.type) {
    case fetchUserProfile.SUCCESS:
    case hydrateUserProfile.SUCCESS:
      return produce(state, (draft) => {
        try {
          const { payload } = action as ReturnType<
            typeof fetchUserProfile['success']
          >;
          const { data } = payload;
          if (!data) return;
          const isLocal = data.auth_provider === 'Local' ? true : false;
          const accountType = isLocal ? state.accountType : data.auth_provider;
          draft.loggedIn = data.logged_in;
          draft.currencyISO = data.currency;
          draft.countryISO = data.country_iso;
          if (data.logged_in) {
            const {
              default_market: defaultMarket,
              feed_sorting: feedSorting,
              show_liquidated: showLiquidated,
              holdings_sorting: holdingsSorting,
              default_portfolio: defaultPortfolio,
              default_watchlist_id: defaultWatchlistId,
              has_seen_sa_modal: hasSeenSAModal,
              has_seen_subscription_modal: hasSeenSubscriptionModal,
              has_seen_trial_end_modal: hasSeenTrialEndModal,
              has_dismissed_wl_banner: isWatchlistBannerDismissed,
              classification_responses: classificationResponses,
              portfolio,
            } = data.settings.data;
            /** If logged_in we can assume these values will be truthy */
            draft.isLocal = isLocal;
            draft.familyName = data.family_name || '';
            draft.id = data.id as string;
            // If the user is logged in, the email should exist by default
            draft.emailExists = true;
            draft.name = data.name as string;
            draft.group = data.group as number;
            draft.givenName = data.given_name as string;
            draft.accountType = accountType as string;
            draft.emailAddress = data.email as string;
            draft.authProvider = data.auth_provider as string;
            draft.referralCode = data.referral_code as string;
            draft.registrationDate = data.register_date as number;
            draft.defaultMarketISO =
              typeof defaultMarket === 'string' &&
              CountryISO[defaultMarket.toUpperCase()]
                ? defaultMarket.toLowerCase()
                : CountryISO.US;
            draft.defaultFeedSorting =
              ((typeof feedSorting === 'string' &&
                feedSorting) as FeedSortingOptions) ||
              FeedSortingOptions.Newest;
            draft.hasSeenSAModal = isBoolean(hasSeenSAModal)
              ? hasSeenSAModal
              : false;
            draft.hasSeenSubscriptionModal = isBoolean(hasSeenSubscriptionModal)
              ? hasSeenSubscriptionModal
              : false;
            draft.hasSeenTrialEndModal = isBoolean(hasSeenTrialEndModal)
              ? hasSeenTrialEndModal
              : false;
            draft.showLiquidated = isBoolean(showLiquidated)
              ? showLiquidated
              : false;
            draft.defaultPortfolioId =
              (defaultPortfolio && defaultPortfolio.toString()) || '';
            draft.defaultWatchlistId =
              (defaultWatchlistId && defaultWatchlistId.toString()) || null;
            draft.holdingsSorting =
              (typeof holdingsSorting === 'string' && holdingsSorting) ||
              HoldingsSortingOptions.CapitalGainWeek;
            draft.isWatchlistBannerDismissed = isBoolean(
              isWatchlistBannerDismissed
            )
              ? isWatchlistBannerDismissed
              : false;
            draft.classificationResponses = classificationResponses
              ? classificationResponses
              : {};
            draft.portfolio = {};
            draft.portfolio.holdingsPeriod = isPeriodType(
              portfolio?.holdings_period
            )
              ? portfolio?.holdings_period
              : undefined;
            draft.portfolio.holdingsSorting = isHoldingsSorting(
              portfolio?.holdings_sorting
            )
              ? portfolio?.holdings_sorting
              : undefined;
            draft.portfolio.links = {};
            for (const portfolioId in portfolio?.links ?? {}) {
              const stateLink = state.portfolio?.links?.[portfolioId];
              const dataLink = portfolio?.links?.[portfolioId];

              draft.portfolio.links[portfolioId] = {
                hasDismissedError:
                  dataLink?.has_dismissed_error ??
                  stateLink?.hasDismissedError ??
                  false,
                hasDismissedSuccess:
                  dataLink?.has_dismissed_success ??
                  stateLink?.hasDismissedSuccess ??
                  false,
              };
            }
          }
        } catch (e) {
          console.error('Reducer: fetchUserProfile.SUCCESS failed', e);
        }
      });
    case updateUserProfile.TRIGGER:
      return {
        ...state,
        updatingProfile: 'details',
      };
    case updateUserProfile.SUCCESS:
    case updateUserProfile.FAILURE:
      return {
        ...state,
        updatingProfile: 'idle',
      };
    default:
      return state;
  }
}
